import { computed, ref, watch, type Component } from 'vue'
import { useRoute } from 'vue-router'
import { User, LayoutGrid, CalendarDays, FileText, ClipboardList, Users, Route } from 'lucide-vue-next'
import { useClientsStore } from '@/stores/clients'
import { usePermissions } from '@/composables/usePermissions'
import { Permission, type PermissionValue } from '@/utils/permissionBitmask'
import { getLoadFailureMessageKey } from '@/utils/errorHandling'
import type { ClientProfile } from '@/api/generated-clients'

/**
 * Shared context for a client dossier (#4256).
 *
 * **This is the one owner of the open dossier's client.** The sidebar (which
 * renders the client-context block — a mini client header plus the dossier nav)
 * and the view being navigated both read it from here, so it is fetched once per
 * dossier entry and there is one copy to keep current. It used to be two: the
 * views loaded the client again through a `useClientLoader` of their own, which
 * cost a second `GET /clients/{id}` on every dossier open and — the sharper edge
 * — let the sidebar go on naming a client by the name an identity edit had
 * already replaced in the view (#4282).
 *
 * Views reach it through {@link useDossierClient}, which words the failure and
 * publishes the breadcrumb label; this composable is what a consumer of the
 * *navigation* model (the sidebar, `ClientDossierTab`) calls.
 */

/** One item of the dossier's sidebar navigation. */
export interface DossierNavItem {
  /** Stable item key. */
  key: string
  /** i18n key for the nav label. */
  labelKey: string
  /** Shown beside the label, and alone where there is no room for one. */
  icon: Component
  /** Route this item navigates to. */
  routeName: string
  /** Route params besides `clientId`. */
  params?: Readonly<Record<string, string>>
  /** Further routes that keep this item active — pages below it with no item of their own. */
  extraRoutes?: readonly string[]
  /** When set, the item is only offered to a user holding at least one of these. */
  permissions?: readonly PermissionValue[]
}

/**
 * The dossier's navigation, in sidebar order — the single declaration
 * behind the sidebar, the section strip below `lg`, the active-item highlight
 * and the section `ClientDossierTab` renders (#4256, replacing the horizontal
 * tab bar of #3509).
 *
 * Profiel leads because that is where clicking a cliënt in the list lands, and
 * Zorgtraject closes the list; neither was in the tab bar, so the sidebar adds
 * navigation rather than only relocating it.
 *
 * `/clients/:clientId/billing` is deliberately not an item: Financiering is
 * reached from its card on Profiel. It is still a dossier route (below), so the
 * sidebar keeps the user's way out of it.
 */
const DOSSIER_NAV_ITEMS: readonly DossierNavItem[] = [
  { key: 'profiel', labelKey: 'clientDossier.tabs.profiel', icon: User, routeName: 'client-detail' },
  {
    key: 'overzicht',
    labelKey: 'clientDossier.tabs.overzicht',
    icon: LayoutGrid,
    routeName: 'client-dossier-overview'
  },
  {
    key: 'afspraken',
    labelKey: 'pages.userAgenda.clientDossier.tabs.appointments',
    icon: CalendarDays,
    routeName: 'client-dossier-appointments',
    // The appointment detail page sits one level below Afspraken, so it keeps
    // that item selected rather than having one of its own.
    extraRoutes: ['client-dossier-appointment-detail']
  },
  {
    key: 'documenten',
    labelKey: 'pages.userAgenda.clientDossier.tabs.documents',
    icon: FileText,
    routeName: 'client-dossier-documents'
  },
  {
    key: 'antwoorden',
    // "Dossiernotities" (#3314) — deliberately the classic label, not the
    // preview tree's "Formulieren", which undoes that rename.
    labelKey: 'pages.userAgenda.clientDossier.tabs.answers',
    icon: ClipboardList,
    routeName: 'client-dossier-answers',
    permissions: [Permission.FORMS_EDIT]
  },
  {
    key: 'zorgkring',
    labelKey: 'pages.userAgenda.clientDossier.tabs.zorgkring',
    icon: Users,
    routeName: 'client-dossier-zorgkring'
  },
  {
    key: 'zorgtraject',
    labelKey: 'clientDossier.tabs.zorgtraject',
    icon: Route,
    routeName: 'client-care-trajectories',
    extraRoutes: ['care-trajectory-detail'],
    // The gate the Snelkoppelingen card applied before the sidebar replaced it.
    permissions: [Permission.CARE_TRAJECTORIES_READ, Permission.CARE_TRAJECTORIES_READ_OWN]
  }
]

/**
 * Dossier routes no nav item owns: the sidebar renders, with nothing
 * marked active. Without this, Financiering would be the one dossier page with
 * no navigation on it.
 */
const UNLISTED_DOSSIER_ROUTES: readonly string[] = ['client-billing']

/**
 * The section `ClientDossierTab` falls back to for a dossier route no item owns.
 *
 * One constant for every fallback on purpose: the three sites this replaced used
 * to disagree, so an unknown route opened Overzicht on mount but *became*
 * Afspraken when it arrived while the dossier was already mounted (#3509).
 */
export const DEFAULT_DOSSIER_VIEW = 'overzicht'

/**
 * Every route this context answers for — derived from the nav items, so a route
 * the sidebar offers cannot be missing from it.
 *
 * Exported so `behandelaar-routes.spec.ts` can assert that no client-scoped
 * route escapes it: a `/clients/:clientId/...` page this does not name gets no
 * sidebar *and*, since #4282, no client — it renders neither the loader, nor an
 * error, nor content. Registering it here (as a nav item, or in
 * {@link UNLISTED_DOSSIER_ROUTES} when it is reached from a card rather than the
 * sidebar) is what makes it a dossier page.
 */
export const DOSSIER_ROUTE_NAMES: readonly string[] = [
  ...UNLISTED_DOSSIER_ROUTES,
  ...DOSSIER_NAV_ITEMS.flatMap((item) => [item.routeName, ...(item.extraRoutes ?? [])])
]

// Shared across every consumer so the client is fetched once per navigation.
const client = ref<ClientProfile | null>(null)
/**
 * What consumers get, so {@link applyClientUpdate} is the only way to write the
 * client rather than merely the intended one — a bare `client.value = updated`
 * from a view is the id guard skipped, which is the defect that guard exists for.
 * A `computed` rather than `readonly()` so the value type is untouched and a
 * template can still pass it as a `ClientProfile` prop.
 */
const readableClient = computed(() => client.value)
const loading = ref(false)
/** i18n key for why the load failed, or `null` while it has not. */
const loadErrorKey = ref<string | null>(null)
let loadedClientId: string | null = null
let inflightClientId: string | null = null
/**
 * Sequence number of the newest fetch. Switching clients faster than the API
 * answers leaves two requests in flight; without this only the *last to resolve*
 * would win, which can be the client the user already navigated away from.
 */
let latestRequest = 0

/**
 * @param force Refetch a client this already holds — what {@link reload} needs
 *   after an archive/unarchive, and the one case the id dedupe must not answer.
 */
async function loadClient(
  clientsStore: ReturnType<typeof useClientsStore>,
  id: string,
  { force = false } = {}
) {
  if (!force) {
    if (loadedClientId === id && client.value) return
    if (inflightClientId === id) return
  }

  const request = ++latestRequest
  inflightClientId = id
  loading.value = true
  loadErrorKey.value = null
  // Drop the previously loaded client so consumers never flash stale data while
  // switching between two clients' dossiers.
  if (loadedClientId !== id) client.value = null

  try {
    const loaded = await clientsStore.getClient(id)
    if (request !== latestRequest) return
    client.value = loaded
    loadedClientId = id
  } catch (err) {
    if (request !== latestRequest) return
    // A dropped connection must not read as "this client does not exist"
    // (SCRIVIO-FE-5W). The store already logged whatever is worth logging.
    loadErrorKey.value = getLoadFailureMessageKey(err, 'clients.notFound')
    client.value = null
    loadedClientId = null
  } finally {
    // A superseded request owns none of this state any more — clearing `loading`
    // here would report the newer fetch as finished.
    if (request === latestRequest) {
      loading.value = false
      inflightClientId = null
    }
  }
}

/**
 * Adopt a freshly saved client profile — the one write path for an identity edit.
 *
 * The edit happens in a view, and the id-based dedupe never refetches a client
 * it already holds, so without this every consumer keeps the name it was fetched
 * with for the rest of the visit.
 *
 * The id guard is what keeps a save that resolved after the user moved on from
 * putting another client's name beside the avatar.
 */
function applyClientUpdate(updated: ClientProfile) {
  if (loadedClientId !== updated.id) return
  client.value = updated
}

export function useDossierContext() {
  const route = useRoute()
  const clientsStore = useClientsStore()
  const { hasAnyPermission } = usePermissions()

  /** Whether a dossier route is currently active. */
  const isActive = computed(() => DOSSIER_ROUTE_NAMES.includes(route.name as string))

  /** The client whose dossier is open — the watch source that drives the one fetch. */
  const clientId = computed(() => (isActive.value ? (route.params.clientId as string) : null))

  /** The nav items, minus the ones this user may not have. */
  const navItems = computed<readonly DossierNavItem[]>(() => {
    if (!isActive.value) return []
    return DOSSIER_NAV_ITEMS.filter(
      (item) => !item.permissions || hasAnyPermission(...item.permissions)
    )
  })

  /** The active item's key, or `''` when no item owns the route. */
  const activeView = computed(() => {
    const name = route.name as string
    const active = DOSSIER_NAV_ITEMS.find(
      (item) => item.routeName === name || item.extraRoutes?.includes(name)
    )
    return active?.key ?? ''
  })

  watch(
    clientId,
    (id) => {
      if (id) {
        loadClient(clientsStore, id)
      } else {
        client.value = null
        loadedClientId = null
      }
    },
    { immediate: true }
  )

  /** Refetch the open dossier's client — what an archive/unarchive needs to see its own effect. */
  async function reload() {
    if (clientId.value) await loadClient(clientsStore, clientId.value, { force: true })
  }

  return {
    isActive,
    client: readableClient,
    loading,
    loadErrorKey,
    activeView,
    navItems,
    reload,
    applyClientUpdate
  }
}
